import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { Col, Modal } from "react-bootstrap";

import Image from "pages/_components/Image";
import Container from "pages/_components/Container";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import { selectors as sessionSelectors } from "reducers/session";
import * as configUtils from "util/config";
import Button from "../Button";
import I18n from "../I18n";

function TokenDisconectModal({ dispatch, showNotificationIn, userId, exchangeToken, shouldUseLocalTimer, loggedUser }) {
    const [showModal, setShowModal] = useState(false);
    const notificationRef = useRef();
    const checkExchangeTokenRef = useRef();
    const expirationRef = useRef();

    const localNotificationDelay = useMemo(() => {
        if (!shouldUseLocalTimer) {
            return 0;
        }
        const exchangeTokenDuration = Number(configUtils.getTimeInMillis("session.exchangeToken.duration"));
        const timeBeforeTimeoutNotification = Number(
            configUtils.getTimeInMillis("session.timeBeforeTimeoutNotification"),
        );
        if (!exchangeTokenDuration || Number.isNaN(exchangeTokenDuration)) {
            return 0;
        }
        if (!timeBeforeTimeoutNotification || Number.isNaN(timeBeforeTimeoutNotification)) {
            return 0;
        }
        const delay = exchangeTokenDuration - timeBeforeTimeoutNotification;
        return delay > 0 ? delay : 0;
    }, [shouldUseLocalTimer]);

    const exchangeTokenDuration = useMemo(() => {
        if (!shouldUseLocalTimer) {
            return 0;
        }
        const duration = Number(configUtils.getTimeInMillis("session.exchangeToken.duration"));
        return duration && !Number.isNaN(duration) ? duration : 0;
    }, [shouldUseLocalTimer]);

    const scheduleLocalTimers = useCallback(() => {
        if (localNotificationDelay > 0) {
            notificationRef.current = setTimeout(() => setShowModal(true), localNotificationDelay);
        }
        if (exchangeTokenDuration > 0) {
            expirationRef.current = setTimeout(() => {
                setShowModal(false);
                dispatch(tokenActions.sessionExpiredRequest(loggedUser));
            }, exchangeTokenDuration);
        }
    }, [localNotificationDelay, exchangeTokenDuration, dispatch]);

    useEffect(() => {
        clearTimeout(notificationRef.current);
        clearInterval(checkExchangeTokenRef.current);
        clearTimeout(expirationRef.current);

        if (shouldUseLocalTimer) {
            scheduleLocalTimers();
        } else if (showNotificationIn || showNotificationIn === 0) {
            notificationRef.current = setTimeout(() => {
                setShowModal(true);
                checkExchangeTokenRef.current = setInterval(() => {
                    dispatch(tokenActions.exchangeTokenCheck(userId, exchangeToken));
                }, 5000);
            }, showNotificationIn);
        } else {
            setShowModal(false);
        }
        return () => {
            clearTimeout(notificationRef.current);
            clearInterval(checkExchangeTokenRef.current);
            clearTimeout(expirationRef.current);
        };
    }, [showNotificationIn, dispatch, userId, exchangeToken, shouldUseLocalTimer, scheduleLocalTimers]);

    const refreshExchangeToken = () => dispatch(tokenActions.refreshTokenRequest(userId, exchangeToken));

    return (
        <div className="modal-container">
            <Modal show={showModal} className="token__modal-disconect-container">
                <Modal.Header>
                    <Container>
                        <Col xs={12} className="justify-content-center d-flex">
                            <div className="token__modal-disconect-icon">
                                <Image src="images/history.svg" />
                            </div>
                        </Col>
                        <Col xs={12} className="justify-content-center d-flex">
                            <I18n
                                id="token.otp.modal.title"
                                component="h2"
                                componentProps={{ className: "token__modal-disconect-title" }}
                            />
                        </Col>
                    </Container>
                </Modal.Header>
                <Modal.Body className="justify-content-center mb-2">
                    <I18n
                        id="token.otp.modal.body"
                        component="p"
                        componentProps={{ className: "token__modal-disconect-body" }}
                    />
                </Modal.Body>
                <Modal.Footer className="px-4">
                    <Button
                        onClick={() => {
                            setShowModal(false);
                            if (shouldUseLocalTimer) {
                                clearTimeout(notificationRef.current);
                                clearTimeout(expirationRef.current);
                                dispatch(tokenActions.updateShowNotificationIn(localNotificationDelay));
                                scheduleLocalTimers();
                            } else {
                                refreshExchangeToken();
                                clearInterval(checkExchangeTokenRef.current);
                            }
                        }}
                        type="button"
                        bsStyle="primary"
                        label="token.otp.modal.button"
                    />
                </Modal.Footer>
            </Modal>
        </div>
    );
}

TokenDisconectModal.propTypes = {
    dispatch: func.isRequired,
    showNotificationIn: number.isRequired,
    userId: string.isRequired,
    exchangeToken: string.isRequired,
    shouldUseLocalTimer: bool,
    loggedUser: shape({}).isRequired,
};

TokenDisconectModal.defaultProps = {
    shouldUseLocalTimer: false,
};

const mapStateToProps = (state) => ({
    showNotificationIn: tokenSelectors.getShowNotificationIn(state),
    userId: tokenSelectors.getUserIdToken(state),
    exchangeToken: tokenSelectors.getExchangeToken(state),
    loggedUser: sessionSelectors.getUser(state),
});

export default connect(mapStateToProps)(TokenDisconectModal);
